<script setup lang="ts">
const { t } = useI18n()
const localePath = useLocalePath()
const route = useRoute()
const { login } = useAuth()

definePageMeta({
  layout: 'default',
  // Skip the form for already-authenticated users. Run as middleware (not in
  // setup) so the redirect happens before the component mounts and avoids
  // top-level-await Suspense issues during hydration.
  middleware(to) {
    const { isLoggedIn } = useAuth()
    const localePath = useLocalePath()
    if (isLoggedIn.value) {
      const raw = typeof to.query.redirect === 'string' ? to.query.redirect : '/'
      return navigateTo(safeInternalPath(raw, localePath('/')))
    }
  }
})

useSeoMeta({
  title: () => t('seo.login.title'),
  description: () => t('seo.login.description'),
  robots: 'noindex, nofollow',
})

const form = ref({
  email: '',
  password: ''
})

const loading = ref(false)
const error = ref('')
const showPassword = ref(false)

const handleLogin = async () => {
  loading.value = true
  error.value = ''

  try {
    await login(form.value.email, form.value.password)

    const target = safeInternalPath(route.query.redirect, localePath('/'))
    navigateTo(target)
  } catch (e: any) {
    error.value = e?.message || t('loginPage.errors.invalidCredentials')
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="bg-gray-50 dark:bg-gray-950 flex justify-center py-12 px-4">
    <div class="w-full max-w-md">
      <!-- Login Form Card -->
      <div class="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-800 p-8">
        <!-- Header -->
        <div class="text-center mb-8">
          <div class="w-16 h-16 bg-brand-100 dark:bg-brand-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <UIcon name="i-lucide-log-in" class="w-8 h-8 text-brand-600 dark:text-brand-400" />
          </div>
          <h1 class="text-2xl font-bold text-gray-900 dark:text-white">
            {{ t('loginPage.title') }}
          </h1>
          <p class="text-gray-500 dark:text-gray-400 mt-2">
            {{ t('loginPage.subtitle') }}
          </p>
        </div>

        <form @submit.prevent="handleLogin" class="space-y-5">
            <!-- Error Message -->
            <div v-if="error" class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-xl flex items-center gap-2">
              <UIcon name="i-lucide-alert-circle" class="w-5 h-5 flex-shrink-0" />
              {{ error }}
            </div>

            <!-- Email -->
            <div>
              <label for="email" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {{ t('loginPage.form.email') }}
              </label>
              <div class="relative">
                <UIcon name="i-lucide-mail" class="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="email"
                  v-model="form.email"
                  type="email"
                  autocomplete="email"
                  required
                  class="w-full pl-12 pr-4 py-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                  :placeholder="t('loginPage.form.emailPlaceholder')"
                />
              </div>
            </div>

            <!-- Password -->
            <div>
              <label for="password" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {{ t('loginPage.form.password') }}
              </label>
              <div class="relative">
                <UIcon name="i-lucide-lock" class="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="password"
                  v-model="form.password"
                  :type="showPassword ? 'text' : 'password'"
                  autocomplete="current-password"
                  required
                  class="w-full pl-12 pr-12 py-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                  :placeholder="t('loginPage.form.passwordPlaceholder')"
                />
                <button
                  type="button"
                  @click="showPassword = !showPassword"
                  class="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <UIcon :name="showPassword ? 'i-lucide-eye-off' : 'i-lucide-eye'" class="w-5 h-5" />
                </button>
              </div>
            </div>

            <!-- Forgot Password -->
            <div class="flex justify-end">
              <NuxtLink :to="localePath('/forgot-password')" class="text-sm font-medium text-brand-600 dark:text-brand-400 hover:underline">
                {{ t('loginPage.form.forgot') }}
              </NuxtLink>
            </div>

            <!-- Submit Button -->
            <button
              type="submit"
              :disabled="loading"
              class="w-full py-4 bg-brand-600 hover:bg-brand-700 disabled:bg-brand-400 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg hover:shadow-xl disabled:cursor-not-allowed"
            >
              <UIcon v-if="loading" name="i-lucide-loader-2" class="w-5 h-5 animate-spin" />
              <UIcon v-else name="i-lucide-log-in" class="w-5 h-5" />
              {{ loading ? t('loginPage.form.loading') : t('loginPage.form.submit') }}
            </button>
          </form>


        <!-- Register Link -->
        <p class="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
          {{ t('loginPage.noAccount') }}
          <NuxtLink :to="{ path: localePath('/register'), query: $route.query.redirect ? { redirect: $route.query.redirect } : {} }" class="font-semibold text-brand-600 dark:text-brand-400 hover:underline">
            {{ t('loginPage.createAccount') }}
          </NuxtLink>
        </p>
      </div>

      <!-- Security Note -->
      <p class="mt-6 text-center text-xs text-gray-500 dark:text-gray-400 flex items-center justify-center gap-1">
        <UIcon name="i-lucide-shield-check" class="w-4 h-4 text-green-500" />
        {{ t('loginPage.security') }}
      </p>
    </div>
  </div>
</template>
